import { useNavigate } from "react-router-dom";
import { Download, FileText, AlertTriangle, Package, ArrowUpRight, ChevronRight } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { StatusBadge, relTime } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";
import { Button } from "@/components/ui/button";
import { useStore, list, controlSummary, departmentBySlug, lagerSummary, deptControlSummaries, restaurantStatus } from "@/lib/dataStore";
import { downloadCSV, downloadPDF } from "@/lib/exporters";

const dateSv = (d) => new Date(d).toLocaleDateString("sv-SE");

const CARD = "bg-card border border-border rounded-2xl shadow-[0_4px_24px_-8px_rgba(20,20,20,0.06)]";

function greeting() {
  const h = new Date().getHours();
  if (h < 10) return "God morgon";
  if (h < 17) return "God eftermiddag";
  return "God kväll";
}

export default function Ledning() {
  useStore();
  const { currentRestaurant, currentOrganization, profile } = useSbAuth();
  const rid = currentRestaurant?.id;
  const org = currentOrganization?.name || "";
  const firstName = (profile?.full_name || "").trim().split(" ")[0];
  const s = rid ? controlSummary(rid) : { total: 0, approved: 0, failed: 0, missed: 0, approvedPct: 0, failedPct: 0, missedPct: 0 };
  const incidents = rid ? list(rid, "incidents") : [];
  const lg = rid ? lagerSummary(rid) : { total: 0, underPar: 0, suppliers: 0 };
  const deptCtrl = rid ? deptControlSummaries(rid) : [];
  const status = rid ? restaurantStatus(rid) : { now: 0, remaining: 0, lastWeekAvg: null };
  const nav = useNavigate();

  const stamp = `${org} · ${currentRestaurant?.name || ""} · ${new Date().toLocaleString("sv-SE")}`;
  const devHeaders = ["Avvikelse", "Avdelning", "Allvarlighet", "Status", "Rapporterad av", "Datum"];
  const devRows = incidents.map((i) => [i.title, departmentBySlug(i.department)?.name || i.department, i.severity || "", i.status || "", i.reporter || "", dateSv(i.created_at)]);

  const today = new Date().toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long" });
  const openIncidents = incidents.filter((i) => i.status !== "closed" && i.status !== "resolved");

  return (
    <div className="max-w-[1100px] mx-auto p-6 sm:p-10 animate-fade-up" data-testid="ledning-page">
      {/* GREETING */}
      <header className="mb-8" data-testid="ledning-greeting">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
          {greeting()}{firstName ? `, ${firstName}` : ""}
        </h1>
        <p className="text-muted-foreground mt-2 capitalize">
          {today}{currentRestaurant?.name ? ` · ${currentRestaurant.name}` : ""}
        </p>
        <div className="flex items-center gap-2 mt-3 text-sm" data-testid="ledning-status">
          <span className="font-semibold text-foreground">Status idag {status.now}%</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">Snitt förra veckan {status.lastWeekAvg != null ? `${status.lastWeekAvg}%` : "samlar data"}</span>
        </div>
      </header>

      {/* KONTROLLER PER AVDELNING — glanceable, no hunting */}
      {deptCtrl.length > 0 && (
        <section className="mb-6" data-testid="dept-controls">
          <p className="overline text-muted-foreground mb-3">Dagens kontroller per avdelning</p>
          <div className="flex flex-wrap gap-2.5">
            {deptCtrl.map((d) => {
              const done = d.done >= d.total;
              return (
                <button key={d.slug} data-testid={`dept-pill-${d.slug}`} onClick={() => nav(`/konto/operation/${d.slug}`)}
                  className={`flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-xl border transition-all hover:-translate-y-0.5 ${done ? "border-success/30 bg-success/5" : "border-warning/40 bg-warning/5"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${done ? "bg-success" : "bg-warning"}`} />
                  <span className="font-medium text-sm">{d.name}</span>
                  <span className={`font-display font-bold text-sm tabular-nums ${done ? "text-success" : "text-warning"}`}>{d.done}/{d.total}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* THREE OVERVIEW CARDS */}
      <div className="grid gap-5 md:grid-cols-3 mb-6">
        <ControlsCard s={s} onClick={() => nav("/konto/operation")} />
        <DeviationsCard incidents={openIncidents} onClick={() => document.getElementById("deviations-section")?.scrollIntoView({ behavior: "smooth" })} />
        <LagerCard lg={lg} onClick={() => nav("/konto/operation/lager", { state: { tab: "produkter" } })} />
      </div>

      {/* DETAILED DEVIATIONS + EXPORT */}
      <ReportSection
        testid="deviations-section"
        icon={<AlertTriangle className="h-4 w-4" />}
        title="Avvikelserapporter"
        count={incidents.length}
        onCSV={() => downloadCSV("avvikelser.csv", devHeaders, devRows)}
        onPDF={() => downloadPDF("Avvikelserapport", stamp, devHeaders, devRows)}
        empty={incidents.length === 0}
        emptyTitle="Allt ser bra ut – inga avvikelser rapporterade"
      >
        <TableHead cols={["Avvikelse", "Avdelning", "Allvarlighet", "Status", "Datum"]} grid="grid-cols-[2fr_1.2fr_1fr_1fr_1fr]" />
        {incidents.map((i) => (
          <button key={i.id} data-testid={`deviation-${i.id}`}
            onClick={() => nav(`/konto/operation/${i.department}`, { state: { tab: "problem", openId: i.id } })}
            className="w-full text-left grid grid-cols-2 md:grid-cols-[2fr_1.2fr_1fr_1fr_1fr] gap-2 px-5 py-3.5 border-b border-border last:border-0 items-center text-sm hover:bg-secondary/50 transition-colors cursor-pointer">
            <span className="font-medium">{i.title}</span>
            <span className="text-muted-foreground">{departmentBySlug(i.department)?.name}</span>
            <span className="capitalize text-muted-foreground">{i.severity}</span>
            <span><StatusBadge status={i.status} /></span>
            <span className="text-muted-foreground font-mono text-xs">{relTime(i.created_at)}</span>
          </button>
        ))}
      </ReportSection>
    </div>
  );
}

/* ---------- Overview cards ---------- */

function ProgressRing({ pct, children }) {
  const r = 46, c = 2 * Math.PI * r;
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 110 110" className="h-full w-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke="hsl(var(--accent))" strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * Math.min(pct, 100)) / 100}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.16,1,0.3,1)" }} />
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">{children}</div>
    </div>
  );
}

function ControlsCard({ s, onClick }) {
  const done = s.approved + s.failed;
  const pct = s.total > 0 ? Math.round((done / s.total) * 100) : 0;
  const allGood = s.total > 0 && s.failed === 0 && s.missed === 0;
  return (
    <button data-testid="summary-total" onClick={onClick}
      className={`${CARD} p-6 text-left flex flex-col hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(20,20,20,0.12)] transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">Dagens kontroller</p>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex items-center gap-5 mt-4" data-testid="summary-bar">
        <ProgressRing pct={pct}>
          <span className="font-display text-2xl font-bold leading-none">{done}<span className="text-muted-foreground">/{s.total}</span></span>
          <span className="text-xs text-muted-foreground mt-1">klara</span>
        </ProgressRing>
        <div className="space-y-2 text-sm">
          <Legend testid="summary-approved" color="bg-success" label="Godkända" value={s.approved} />
          <Legend testid="summary-failed" color="bg-warning" label="Underkända" value={s.failed} />
          <Legend testid="summary-missed" color="bg-danger" label="Missade" value={s.missed} />
        </div>
      </div>
      <p className={`text-sm mt-4 font-medium ${allGood ? "text-success" : "text-muted-foreground"}`}>
        {s.total === 0 ? "Inga kontroller schemalagda idag" : allGood ? "Allt ser bra ut – bra jobbat!" : `${s.missed + s.failed} behöver åtgärdas`}
      </p>
    </button>
  );
}

function Legend({ color, label, value, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold ml-auto tabular-nums">{value}</span>
    </div>
  );
}

function DeviationsCard({ incidents, onClick }) {
  const top = incidents.slice(0, 3);
  return (
    <button data-testid="deviations-card" onClick={onClick}
      className={`${CARD} p-6 text-left flex flex-col hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(20,20,20,0.12)] transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">Öppna avvikelser</p>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="font-display text-4xl font-bold mt-4 tracking-tight">{incidents.length}</p>
      {top.length > 0 ? (
        <div className="mt-4 space-y-2.5">
          {top.map((i) => (
            <div key={i.id} className="flex items-center gap-2.5 text-sm">
              <span className={`h-2 w-2 rounded-full shrink-0 ${i.severity === "high" ? "bg-danger" : i.severity === "medium" ? "bg-warning" : "bg-muted-foreground"}`} />
              <span className="truncate">{i.title}</span>
            </div>
          ))}
          {incidents.length > 3 && <p className="text-xs text-muted-foreground pt-1">+{incidents.length - 3} till</p>}
        </div>
      ) : (
        <p className="text-sm text-success font-medium mt-4">Allt ser bra ut just nu</p>
      )}
    </button>
  );
}

function LagerCard({ lg, onClick }) {
  const ok = lg.underPar === 0;
  return (
    <button data-testid="open-lager" onClick={onClick}
      className={`${CARD} p-6 text-left flex flex-col hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(20,20,20,0.12)] transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground flex items-center gap-2"><Package className="h-4 w-4" /> Lager</p>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className={`font-display text-4xl font-bold mt-4 tracking-tight ${ok ? "" : "text-warning"}`}>
        {ok ? lg.total : lg.underPar}
      </p>
      <p className="text-sm text-muted-foreground mt-1">{ok ? "produkter i lager" : "under par-nivå"}</p>
      <p className={`text-sm mt-4 font-medium ${ok ? "text-success" : "text-warning"}`}>
        {ok ? "Lagernivåer OK" : "Dags att beställa"}
      </p>
      <p className="text-xs text-muted-foreground mt-2">{lg.total} produkter · {lg.suppliers} leverantörer</p>
    </button>
  );
}

/* ---------- Deviations table section ---------- */

function ReportSection({ icon, title, count, onCSV, onPDF, children, empty, emptyTitle, testid }) {
  return (
    <section id={testid} data-testid={testid} className="mb-10 scroll-mt-6">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <p className="overline text-muted-foreground flex items-center gap-2">{icon} {title} <span className="text-foreground">({count})</span></p>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={onCSV} disabled={empty} data-testid={`${testid}-csv`}><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button size="sm" variant="secondary" onClick={onPDF} disabled={empty} data-testid={`${testid}-pdf`}><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      </div>
      {empty ? <EmptyState title={emptyTitle} /> : (
        <div className={`${CARD} overflow-hidden`}>{children}</div>
      )}
    </section>
  );
}

function TableHead({ cols, grid }) {
  return (
    <div className={`hidden md:grid ${grid} gap-2 px-5 py-3 text-xs text-muted-foreground border-b border-border uppercase tracking-wide`}>
      {cols.map((c) => <span key={c}>{c}</span>)}
    </div>
  );
}
